import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { toast } from "@/hooks/use-toast";

/**
 * FounderLinkStatus
 * ----------------------------------------------------------------
 * Surfaces whether the signed-in account is linked to a `founders`
 * row, plus the most recent auto-link audit entry. Includes a
 * one-click retry that re-runs `fn_link_founder_by_email`.
 */
export const FounderLinkStatus = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [linked, setLinked] = useState<boolean | null>(null);
  const [linkedEmail, setLinkedEmail] = useState<string | null>(null);
  const [lastLinkedAt, setLastLinkedAt] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const [{ data: founderRow }, { data: auditRow }] = await Promise.all([
      supabase.from("founders").select("email").eq("user_id", user.id).maybeSingle(),
      supabase
        .from("founder_link_audit" as never)
        .select("linked_at")
        .eq("user_id", user.id)
        .order("linked_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    setLinked(!!founderRow);
    setLinkedEmail((founderRow as { email?: string } | null)?.email ?? null);
    setLastLinkedAt(
      (auditRow as { linked_at?: string } | null)?.linked_at ?? null,
    );
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const retry = async () => {
    setRetrying(true);
    const { data, error } = await supabase.rpc("fn_link_founder_by_email" as never);
    setRetrying(false);
    if (error) {
      toast({
        title: "Link attempt failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    if (data) {
      toast({ title: "Founder row linked" });
    } else {
      toast({
        title: "No link performed",
        description:
          "Either already linked, no matching founder email, or your auth email isn't verified yet.",
      });
    }
    await refresh();
  };

  return (
    <Card className="p-4 flex items-start gap-4 flex-wrap">
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          {loading ? (
            <Badge variant="outline">Checking…</Badge>
          ) : linked ? (
            <Badge className="gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" /> Linked
            </Badge>
          ) : (
            <Badge variant="destructive" className="gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> Unlinked
            </Badge>
          )}
          <span className="text-sm font-medium">Founder row</span>
        </div>
        <p className="text-xs text-muted-foreground">
          {loading
            ? "Loading link status…"
            : linked
            ? `Auto-link succeeded for ${linkedEmail ?? "your founder email"}.${
                lastLinkedAt
                  ? ` Last linked ${new Date(lastLinkedAt).toLocaleString()}.`
                  : ""
              }`
            : "No founders row is linked to your account yet. Verify your auth email matches a founder record, then retry."}
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={retry}
        disabled={retrying || loading}
        className="gap-1.5"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${retrying ? "animate-spin" : ""}`} />
        {retrying ? "Retrying…" : "Retry link"}
      </Button>
    </Card>
  );
};

export default FounderLinkStatus;
